<template lang="pug">
div
  slot(name="activator", :handleSheet="handleSheet")
    Button(
      :btnLabel="btnLabel",
      @click="handleSheet(true)",
      :block="isXsScreen"
    )
  Sheet(:showSheet="showSheet", @changeVisibility="handleSheet")
    template(v-if="showSheet")
      FormLayout(:formTitle="formTitle")
        template(v-slot:headerActions)
          div(style="height: 100%")
            TextButton.mr-2(
              :label="t$('.cancel')",
              @click="handleSheet(false)"
            )
            Button(
            :disabled="disableSaveButton",
              :btnLabel="t$('.save')",
              @click="handleSave()"
            )
        template(v-slot:headerAlert)
          TopicFormAlert(
            :fieldsWithErrors="fieldsWithErrors",
            :defaultText="alertText",
            :requestError="currentTopic.error"
          )
        template(v-slot:main)
          TopicFormPanels(
            :permittedPanels="permittedPanels",
            :activePanels="activePanels",
            :isEdit="isEdit"
            :viewContext="viewContext"
          )
        template(v-slot:footerActions)
          TextButton.mr-2(
            :label="t$('.cancel')",
            @click="handleSheet(false)"
          )
          Button(
            :btnLabel="t$('.save')",
            @click="handleSave()"
              :disabled="disableSaveButton",
          )
        template(v-slot:footerAlert)
          TopicFormAlert(
            :fieldsWithErrors="fieldsWithErrors",
            :defaultText="alertText",
            :requestError="currentTopic.error"
          )
</template>

<script lang="ts">
import Sheet from "../UI/Sheet.vue"
import Button from "../UI/Button.vue"
import FormLayout from "../UI/FormLayout.vue"
import TextButton from "../UI/TextButton.vue"
import TopicFormAlert from "./TopicFormAlert.vue"
import TopicFormPanels from "./TopicFormPanels.vue"
import { i18n } from "application"
import { createNamespacedHelpers } from "vuex"
import {
  computed,
  ComputedRef,
  defineComponent,
  getCurrentInstance,
  reactive,
  ref,
  Ref,
  watch,
} from "vue"
import { ViewContext } from "concerns/useContextName"
import { useCanSendUrgentOnLevelOrClientFallback } from "features/common/permissions/usePermissionCheckHelper"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { FileAndId } from "app/modules/core/types/dtos"
import { useGroupContext } from "features/group/groupContext"

export type FieldWithErrors = {
  selector: string
  message: string
  panel?: string
  fieldName?: string
}

const {
  mapActions: mapTopicActions,
  mapState: mapTopicState,
  mapMutations: mapTopicMutations,
} = createNamespacedHelpers("topic")

export default defineComponent({
  name: "TopicForm",
  components: {
    Sheet,
    Button,
    FormLayout,
    TextButton,
    TopicFormPanels,
    TopicFormAlert,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      showSheet: false,
      isSaving: false,
    }
  },
  computed: {
    currentGroupIdFromRoute(): string | null {
      return this.$route.params.group_id || null
    },
    currentTopicIdFromRoute(): string | null {
      return this.$route.params.topic_id || null
    },
    btnLabel(): string {
      return this.t$(".new_topic")
    },
    formTitle(): string {
      return this.isEdit ? this.t$(".edit_topic") : this.t$(".new_topic")
    },
    alertText(): string {
      return this.t$(".topic_alert_info")
    },
    isXsScreen(): boolean {
      return this.$vuetify.breakpoint.name === "xs"
    },
    disableSaveButton(): boolean {
      return this.isSaving
    },
    ...mapTopicState(["currentTopic"]),
  },
  methods: {
    handleSheet(val) {
      this.showSheet = val
    },
    openSheetAndScrollToId(selector) {
      this.showSheet = true
      setTimeout(() => {
        const el = document.getElementById(selector)
        if (el) el.scrollIntoView({ behavior: "smooth" })
      }, 500)
    },
    navigateToTopic(groupId, topicId) {
      window.open(`/groups/${groupId}/topics/${topicId}`, "_self")
    },
    async handleSave() {
      this.isSaving = true
      const isValid = this.validateBeforeSave()

      if (isValid) {
        if (this.isEditCurrently) {
          await this.editTopic()
        } else {
          await this.createNewTopic()
        }
      }
      this.isSaving = false
    },
    validateBeforeSave() {
      this.fieldsWithErrors = this.validateFields(this.validationFields)
      const panelsWithValidationErrors = this.getPanelsWithValidationErrors(
        this.permittedPanels,
        this.fieldsWithErrors
      )
      if (panelsWithValidationErrors.length) {
        this.activePanels = [
          ...this.initialActivePanels,
          ...panelsWithValidationErrors,
        ]
      }
      return !this.fieldsWithErrors.length
    },
    async createNewTopic() {
      this.isEditCurrently = await this.createTopic(this.requestBody)
      if (!this.currentTopic?.error && this.currentTopic?.data?.id) {
        this.handleSheet(false)
        this.navigateToTopic(
          this.currentTopic?.data?.group?.id,
          this.currentTopic?.data?.id
        )
      }
    },
    async editTopic() {
      await this.updateTopic({
        topicId: this.currentTopicIdFromRoute ?? this.currentTopic?.data?.id,
        requestBody: this.requestBody,
      })
      if (!this.currentTopic?.error) {
        this.handleSheet(false)
        this.navigateToTopic(
          this.currentGroupIdFromRoute ?? this.currentTopic?.data?.group?.id,
          this.currentTopicIdFromRoute ?? this.currentTopic?.data?.id
        )
      }
    },
    ...mapTopicActions(["updateTopic", "createTopic"]),
    ...mapTopicMutations(["SET_TOPIC_ERROR"]),
  },
  setup(props, context) {
    const isEditCurrently: Ref<boolean> = ref(props.isEdit)

    watch(
      () => props.isEdit,
      (newVal) => {
        isEditCurrently.value = newVal
      }
    )

    //input fields default values
    let formData = reactive({
      inputTitle: "",
      inputLevelOrClient: {
        type: null,
        value: null,
      },
      inputGroup: null,
      inputAccessVal: null,
      inputTags: [],
      inputText: "",
      inputRemind: false,
      remindMailedAt: null,
      remindMailedBy: null,
      inputReminder: null,
    })

    const setFormData = (value) => {
      formData[value.key] = value.val
    }

    const root = getCurrentInstance().proxy

    //Default dropdown options for inputLevelOrClient field
    const levelOrClientOptions = computed(() => {
      return root.$store.getters["member/memberScope"]
    })

    //when member api data has arrived and levelOrClientOptions are set
    //set inputLevelOrClient to current client as a initial value
    watch(
      levelOrClientOptions,
      (newVal) => {
        if (newVal) {
          let currentClient = newVal.find((l) => l.type === "Client")
          formData.inputLevelOrClient = currentClient
        }
      },
      { immediate: true }
    )

    //when group api data has arrived then the currentpage is a grouppage
    //set group level as the inputLevelOrClient
    //set grouppage group as groupinput
    const groupContext = useGroupContext()
    const currentPageGroup = groupContext.currentGroup
    watch(
      currentPageGroup,
      (newPageGroupVal) => {
        if (newPageGroupVal) {
          formData.inputLevelOrClient = findClientOrLevelOfPageGroup(
            levelOrClientOptions?.value,
            newPageGroupVal
          )
          formData.inputGroup = newPageGroupVal
        }
      },
      { immediate: true }
    )

    function findClientOrLevelOfPageGroup(levelsAndClientList, group) {
      if (group.level_id) {
        return levelsAndClientList.find(
          (l) => l.type === "Level" && l.value.id === group.level_id
        )
      }
      return levelsAndClientList.find((l) => l.type === "Client")
    }

    //when the topic api data has arrived
    //then the current page is a topic page and this form is an edit form
    //and the topic data should be set to the input fields
    const currentTopic = computed(() => {
      return root.$store.state.topic?.currentTopic?.data
    })

    const canEdit = computed(() => {
      return root.$store.getters["member/canEditTopicOnClient"]
    })

    watch(
      currentTopic,
      (newTopicVal) => {
        if (newTopicVal?.id) {
          formData.inputTitle = newTopicVal?.title
          formData.inputAccessVal = newTopicVal?.nonmember_access
          formData.inputGroup = newTopicVal?.group
          formData.inputTags = newTopicVal?.topic_tag_list
          formData.inputText = newTopicVal?.text
          formData.inputRemind = newTopicVal?.urgent
          formData.remindMailedAt = newTopicVal?.mailed_at
          formData.remindMailedBy = newTopicVal?.mailed_by
        }
      },
      { immediate: true }
    )

    //Validation
    const inputTitleRules = [
      (v) =>
        (!!v && !!v.length) ||
        i18n.t("components.topic_form.validate_titleInput"),
    ]
    const inputTextRules = [
      (v) => {
        const isEmpty = !v || !v.length || v.toString() === "<p></p>"
        return isEmpty
          ? i18n.t("components.topic_form.validate_textInput")
          : true
      },
    ]
    const inputGroupRules = [
      (v) =>
        (!!v && !!v.name.length) ||
        i18n.t("components.topic_form.validate_groupInput"),
    ]
    let fieldsWithErrors: Ref<FieldWithErrors[]> = ref([])
    const validationFields = computed(() => [
      {
        fieldName: "inputTitle",
        panel: "general-fields",
        selector: "topic-form__titleInput",
        rules: inputTitleRules,
      },
      {
        fieldName: "inputGroup",
        panel: "general-fields",
        selector: "topic-form__groupInput",
        rules: inputGroupRules,
      },
      {
        fieldName: "inputText",
        panel: "description",
        selector: "topic-form__textInput",
        rules: inputTextRules,
      },
    ])

    const attachmentsValid = ref(true)

    function validateFields(fields) {
      let errorList = []
      fields.forEach((field) => {
        let message = ""
        field.rules.forEach((rule) => {
          let errorMessage = rule(formData[field.fieldName])
          if (typeof errorMessage == "string") {
            message += errorMessage
          }
        })
        if (message) {
          errorList.push({
            panel: field.panel,
            fieldName: field.fieldName,
            selector: field.selector,
            message,
          })
        }
      })

      if (!attachmentsValid.value) {
        errorList.push({
          panel: "description",
          selector: "topic-form__textInput",
          message:
            "Warte bis alle Anhänge hochgeladen sind oder entferne fehlerhafte Anhänge.",
        })
      }

      return errorList
    }

    function getPanelsWithValidationErrors(panelsList, fieldsWithErrors) {
      const panelNames = fieldsWithErrors.map((errorField) => errorField.panel)
      const panelIndexes = panelsList.reduce(
        (prevVal, currPanel, currIndex) => {
          if (panelNames.includes(currPanel.name)) {
            prevVal.push(currIndex)
          }
          return prevVal
        },
        []
      )
      return panelIndexes
    }
    //permissions for panels
    let canSendUrgent = useCanSendUrgentOnLevelOrClientFallback(
      formData.inputGroup?.level_id
    )
    watch(
      () => formData.inputGroup,
      (newVal) => {
        canSendUrgent = useCanSendUrgentOnLevelOrClientFallback(
          newVal?.level_id
        )
      },
      { immediate: true }
    )

    const viewContext: ComputedRef<ViewContext> = computed(() => {
      const type = formData?.inputLevelOrClient?.type
      if (type === "Level") {
        return {
          group_id: formData?.inputGroup?.id,
          level: formData.inputLevelOrClient.value,
        }
      } else {
        return {
          group_id: formData?.inputGroup?.id,
          client: formData.inputLevelOrClient.value,
        }
      }
    })

    const onFileAddedListener = ref<(fileAndId: FileAndId) => void>(null)
    const addFile = (fileAndId: FileAndId) => {
      if (onFileAddedListener.value) {
        onFileAddedListener.value(fileAndId)
      }
    }

    //panels
    const panels = computed(() => {
      return [
        {
          name: "general-fields",
          selector: "suus__topic-form__general-fields",
          title: "Allgemein",
          componentName: "TopicFormPanelGeneralFields",
          componentProps: {
            inputTitle: formData.inputTitle,
            inputTitleValidation: inputTitleRules,
            inputGroup: formData.inputGroup,
            inputGroupValidation: inputGroupRules,
            inputLevelOrClient: formData.inputLevelOrClient,
            inputTags: formData.inputTags,
            inputAccessVal: formData.inputAccessVal,
            levelOrClientOptions: levelOrClientOptions.value,
            isEdit: props.isEdit,
          },
          updateField: setFormData,
          isActive: true,
          permitted: true,
        },
        {
          name: "description",
          selector: "suus__topic-form__description",
          title: "Diskussionstext",
          componentName: "TopicFormPanelDescription",
          componentProps: {
            inputText: formData.inputText,
            inputTextValidation: inputTextRules,
            viewContext: viewContext.value,
            updateValidationState: (isValid: boolean) => {
              attachmentsValid.value = isValid
            },
            addFile: addFile,
            topicId: currentTopic.value?.id,
          },
          updateField: setFormData,
          isActive: true,
          permitted: true,
        },
        {
          name: "reminder",
          selector: "suus__topic-form__reminder",
          title: "Benachrichtigungen",
          componentName: "TopicFormPanelReminder",
          componentProps: {
            inputReminder: formData.inputReminder,
            remindMailedAt: formData.remindMailedAt,
            remindMailedBy: formData.remindMailedBy,
          },
          updateField: setFormData,
          isActive: canSendUrgent.value,
          permitted: canSendUrgent.value,
        },
        {
          name: "dangerzone",
          selector: "suus__topic-form__dangerzone",
          title: "Gefahrenzone",
          componentName: "DangerZone",
          componentProps: {
            dangerZoneTitle: i18n.t("components.topic_form.delete_topic"),
            dangerZoneText: i18n.t("components.topic_form.delete_topic_text"),
            dangerZoneButtonLabel: i18n.t("components.topic_form.delete"),
            confirmationTitle: i18n.t(
              "components.topic_form.delete_topic_confirmation_title"
            ),
            confirmationText: i18n.t(
              "components.topic_form.delete_topic_confirmation_text"
            ),
            handleConfirmation: handleTopicDeleteConfirmation,
          },
          updateField: () => null,
          isActive: props.isEdit,
          permitted: props.isEdit && canEdit.value,
        },
      ]
    })
    const permittedPanels = computed(() => {
      return panels.value.filter((panel) => panel.permitted)
    })
    const initialActivePanels = computed(() => {
      const list = permittedPanels.value.reduce(
        (prevVal, currPanel, currIndex) => {
          if (currPanel.isActive) prevVal.push(currIndex)
          return prevVal
        },
        []
      )
      return list
    })

    let activePanels = ref(initialActivePanels.value)

    function handleTopicDeleteConfirmation(
      getTopicId = () => currentTopic.value?.id
    ) {
      root.$store.dispatch("topic/deleteTopic", getTopicId()).then(() => {
        window.history.back()
        window.location.reload()
      })
    }

    //Compose request body for API
    function composeTagsString(tagsList) {
      return tagsList.join(",")
    }

    const requestBody = computed(() => {
      return {
        title: formData.inputTitle,
        nonmember_access: formData.inputAccessVal,
        group: formData.inputGroup,
        group_id: formData.inputGroup?.id,
        topic_tag_list: composeTagsString(formData.inputTags),
        text: formData.inputText,
        urgent: formData.inputRemind,
      }
    })

    const { t$ } = useTranslate("components.topic_form")

    return {
      levelOrClientOptions,
      viewContext,
      requestBody,
      validationFields,
      validateFields,
      fieldsWithErrors,
      activePanels,
      permittedPanels,
      getPanelsWithValidationErrors,
      initialActivePanels,
      isEditCurrently,
      t$,
    }
  },
})
</script>
